import { useQuery } from '@apollo/client/react';
import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableAddButton } from '@optools/components/data-table/data-table-add-button';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { ScrollPage } from '@optools/components/layout/layout';
import { PageLoader } from '@optools/components/loading/page-loader';
import { GET_ACTIVE_LOCATION_OVERRIDES } from '@optools/services/operator-gateway/ip-override/queries';
import { LocationOverrideView } from '@optools/services/operator-gateway/types/generated/graphql';
import { CreateOverrideDialog } from '@optools/tools/ip-override/components/create-override-dialog';
import { StatusBadge } from '@optools/tools/ip-override/components/status-badge';
import { getOverrideStatus } from '@optools/tools/ip-override/utils';
import { formatTimestamp } from '@optools/utils/datetime';
import { createFileRoute } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';

export const Route = createFileRoute('/_auth/ip-override/overrides')({
  component: RouteComponent,
});

const columns: ColumnDef<LocationOverrideView>[] = [
  {
    accessorKey: 'countryCode',
    cell: ({ cell }) => cell.row.original.countryCode.toUpperCase(),
    header: ({ column }) => <DataTableColumnHeader column={column} title="Country Code" />,
    id: 'Country Code',
  },
  {
    accessorKey: 'oktaId',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Okta ID" />,
    id: 'Okta ID',
  },
  {
    accessorKey: 'ipAddress',
    header: ({ column }) => <DataTableColumnHeader column={column} title="IP Address" />,
    id: 'IP Address',
  },
  {
    accessorKey: 'zipCode',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Zip Code" />,
    id: 'Zip Code',
  },
  {
    accessorFn: ({ expiration }) => getOverrideStatus(expiration),
    cell: ({ cell }) => <StatusBadge expiration={cell.row.original.expiration} />,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    id: 'Status',
  },
  {
    accessorKey: 'expiration',
    cell: ({ cell }) => formatTimestamp(cell.row.original.expiration),
    header: ({ column }) => <DataTableColumnHeader column={column} title="Expiration" />,
    id: 'Expiration',
  },
  {
    accessorKey: 'createdAt',
    cell: ({ cell }) => formatTimestamp(cell.row.original.createdAt),
    header: ({ column }) => <DataTableColumnHeader column={column} title="Created At" />,
    id: 'Created At',
  },
];

export function RouteComponent() {
  const { data, loading } = useQuery(GET_ACTIVE_LOCATION_OVERRIDES);

  const overrides = data?.getActiveLocationOverrides.overrides ?? [];

  if (loading) return <PageLoader />;

  return (
    <ScrollPage>
      <DataTable className="[&_td]:p-4!" columns={columns} data={overrides}>
        <CreateOverrideDialog>
          {({ onClick }) => (
            <DataTableAddButton onClick={onClick}>Create Override</DataTableAddButton>
          )}
        </CreateOverrideDialog>
      </DataTable>
    </ScrollPage>
  );
}
